import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { CrawlerStatus } from 'src/app/models/integration/crawler';

/**
 * Status chip for a crawl. Computes its CSS class + icon from the status,
 * and shows an explanatory tooltip. Renders "-" when no status is provided.
 */
@Component({
  selector: 'app-crawl-status',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TranslateModule],
  templateUrl: './crawl-status.component.html',
  styleUrls: ['./crawl-status.component.scss'],
})
export class CrawlStatusComponent {
  readonly status = input<CrawlerStatus | undefined>();

  // Transient statuses that get the animated chip + a leading icon.
  private static readonly ACTIVE: CrawlerStatus[] = [
    CrawlerStatus.PENDING,
    CrawlerStatus.CRAWLING,
    CrawlerStatus.UPLOADING,
    CrawlerStatus.DISCOVERING,
  ];

  private static readonly CLASS_MAP: Record<CrawlerStatus, string> = {
    [CrawlerStatus.PENDING]: 'light',
    [CrawlerStatus.CRAWLING]: 'main animated',
    [CrawlerStatus.UPLOADING]: 'main animated',
    [CrawlerStatus.COMPLETED]: 'green plain',
    [CrawlerStatus.FAILED]: 'red plain',
    [CrawlerStatus.PARTIALLY_FAILED]: 'orange plain',
    [CrawlerStatus.DISCOVERING]: 'main animated',
    [CrawlerStatus.CANCELED]: 'orange plain',
  };

  readonly cssClass = computed(() => {
    const s = this.status();
    return s ? (CrawlStatusComponent.CLASS_MAP[s] ?? 'basic') : 'basic';
  });

  readonly isActive = computed(() => {
    const s = this.status();
    return !!s && CrawlStatusComponent.ACTIVE.includes(s);
  });

  readonly icon = computed(() => {
    switch (this.status()) {
      case CrawlerStatus.CRAWLING:
        return 'travel_explore';
      case CrawlerStatus.DISCOVERING:
        return 'language';
      default:
        return 'cloud_upload';
    }
  });
}
